import { Button } from '@/components/Button';
import { Loading } from '@/components/Loading';
import { Text } from '@/components/Text';
import { useSubscription } from '@/hooks/useSubscription';
import { useCardStore } from '@/stores/useCardStore';
import { useStatisticsStore } from '@/stores/useStatisticsStore';
import { CardStatistics, EvolutionByMonth } from '@/types/statistic';
import { getIconForLink } from '@/utils/getIconForLink';
import { handleError } from '@/utils/handleError';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BsQrCodeScan } from 'react-icons/bs';
import { FiBarChart2, FiPieChart, FiStar, FiTrendingUp } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
const formatMonth = (month: string) => month.substring(0, 3);

interface StatCardProps {
  title: string;
  value: number;
  subtitle?: string;
}

interface PieTooltipProps {
  active?: boolean;
  payload?: Array<{
    payload: {
      title: string;
      icon: string;
      visits_count: number;
    };
  }>;
}

interface LegendProps {
  payload?: Array<{
    value: string;
    color: string;
    payload: {
      icon: string;
      title: string;
    };
  }>;
}

const StatCard = ({ title, value, subtitle }: StatCardProps) => {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 transition-colors">
      <h3 className="text-sm font-medium text-gray-500">{title}</h3>
      <div className="mt-2 text-2xl font-bold text-gray-800">
        {value.toLocaleString()}
      </div>
      {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
    </div>
  );
};

const COLORS = [
  '#0ea5e9', // sky-500
  '#3b82f6', // linko-light
  '#6366f1', // indigo-500
  '#8b5cf6', // violet-500
  '#d946ef', // fuchsia-500
  '#ec4899', // pink-500
  '#f43f5e', // rose-500
  '#ef4444', // red-500
  '#f97316', // orange-500
  '#eab308', // yellow-500
  '#22c55e', // green-500
  '#10b981', // emerald-500
];

const CustomPieTooltip = ({ active, payload }: PieTooltipProps) => {
  const { t } = useTranslation('updateCard');

  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-2">
        <div className="flex items-center space-x-1.5">
          <img
            src={getIconForLink(data.icon)}
            alt={data.title}
            className="size-5"
          />
          <span className="text-sm font-medium">{data.title}</span>
        </div>
        <p className="mt-1 text-xs text-gray-600">
          {data.visits_count.toLocaleString()}{' '}
          {t('statistics.cards.linksClicks')}
        </p>
      </div>
    );
  }
  return null;
};

const CustomLegend = ({ payload }: LegendProps) => {
  if (!payload) return null;

  // Limiter le nombre d'éléments affichés si trop nombreux
  const maxItems = payload.length > 8 ? 8 : payload.length;
  const displayedItems = payload.slice(0, maxItems);
  const hasMoreItems = payload.length > maxItems;

  return (
    <div className="mt-1 flex max-h-[130px] flex-wrap justify-center gap-1.5 overflow-y-auto px-1 pb-1">
      {displayedItems.map((entry, index) => (
        <div
          key={`legend-${index}`}
          className="flex items-center space-x-1 rounded-md border border-gray-100 bg-white px-1.5 py-0.5"
        >
          <div
            className="size-2 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <img
            src={getIconForLink(entry.payload.icon)}
            alt={entry.payload.title}
            className="size-3"
          />
          <span className="max-w-[60px] truncate text-xs text-gray-600 sm:max-w-[100px]">
            {entry.payload.title}
          </span>
        </div>
      ))}
      {hasMoreItems && (
        <div className="px-1 text-xs italic text-gray-500">
          +{payload.length - maxItems}{' '}
          {payload.length - maxItems > 1 ? 'autres' : 'autre'}
        </div>
      )}
    </div>
  );
};

interface PremiumStatisticsOverlayProps {
  isVisible: boolean;
  onUpgrade: () => void;
}

const PremiumStatisticsOverlay = ({
  isVisible,
  onUpgrade,
}: PremiumStatisticsOverlayProps) => {
  const { t } = useTranslation('updateCard');

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="absolute inset-0 z-10 flex flex-col items-center justify-start overflow-y-auto bg-white/90 p-6 backdrop-blur-sm"
    >
      <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white px-5 py-8 text-center sm:px-8">
        <div className="mb-6 flex justify-center">
          <motion.div
            className="sm:size-18 flex size-16 items-center justify-center rounded-full bg-gray-50 text-gray-800"
            animate={{
              scale: [1, 1.05, 1],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              repeatType: 'reverse',
            }}
          >
            <FiStar className="size-8 text-gray-600 sm:size-9" />
          </motion.div>
        </div>

        <h2 className="mb-3 text-xl font-semibold text-gray-800 sm:text-2xl">
          {t('statistics.premium.title')}
        </h2>

        <Text className="mb-8 text-gray-600">
          {t('statistics.premium.description')}
        </Text>

        <div className="mb-8 grid grid-cols-2 gap-4">
          <div className="flex flex-col items-center rounded-lg border border-gray-200 bg-white p-3">
            <FiTrendingUp className="mb-2 size-6 text-gray-700 sm:size-7" />
            <Text className="text-sm font-medium text-gray-600">
              {t('statistics.premium.feature1')}
            </Text>
          </div>
          <div className="flex flex-col items-center rounded-lg border border-gray-200 bg-white p-3">
            <FiPieChart className="mb-2 size-6 text-gray-700 sm:size-7" />
            <Text className="text-sm font-medium text-gray-600">
              {t('statistics.premium.feature2')}
            </Text>
          </div>
          <div className="flex flex-col items-center rounded-lg border border-gray-200 bg-white p-3">
            <FiBarChart2 className="mb-2 size-6 text-gray-700 sm:size-7" />
            <Text className="text-sm font-medium text-gray-600">
              {t('statistics.premium.feature3')}
            </Text>
          </div>
          <div className="flex flex-col items-center rounded-lg border border-gray-200 bg-white p-3">
            <BsQrCodeScan className="mb-2 size-6 text-gray-700 sm:size-7" />
            <Text className="text-sm font-medium text-gray-600">
              {t('statistics.premium.feature4')}
            </Text>
          </div>
        </div>

        <Button
          onClick={onUpgrade}
          className="w-full rounded-lg border border-gray-300 bg-gray-50 py-3 text-sm font-medium text-gray-800 transition-colors hover:bg-gray-100"
        >
          {t('statistics.premium.upgradeButton')}
        </Button>
      </div>
    </motion.div>
  );
};

export const UpdateCardStatistics = () => {
  const { i18n } = useTranslation();
  const { t } = useTranslation('updateCard');
  const { card } = useCardStore();
  const { cardStatistics, isLoading, fetchCardStatistics } =
    useStatisticsStore();
  const navigate = useNavigate();
  const { hasActiveSubscription } = useSubscription(card?.user ?? null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);

    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  useEffect(() => {
    // Helper function to generate monthly data for charts
    const generateMonthlyData = (multiplier = 1): EvolutionByMonth[] => {
      const months = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ];
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth();

      return Array.from({ length: 6 }, (_, i) => {
        const monthIndex = (currentMonth - 5 + i + 12) % 12;
        const year = currentYear - (monthIndex > currentMonth ? 1 : 0);
        const randomValue = Math.floor(Math.random() * 30 * multiplier) + 5;

        return {
          month: months[monthIndex],
          count: randomValue,
          year: year,
        };
      });
    };

    const fetchData = async () => {
      if (card) {
        try {
          await fetchCardStatistics(card.slug);
        } catch (error) {
          const err = error as { response?: { data?: { message?: string } } };
          // Check if error is premium_required
          if (err.response?.data?.message?.startsWith('premium_required')) {
            // Generate fake data for charts
            const fakeData: CardStatistics = {
              cardVisitsCount: 125,
              accessoriesScansCount: [
                { target_id: 1, uuid: 'uuid-1', scan_count: 42 },
                { target_id: 2, uuid: 'uuid-2', scan_count: 28 },
              ],
              cardLinksCount: [
                {
                  target_id: 1,
                  title: 'LinkedIn',
                  icon: 'linkedin',
                  visits_count: 35,
                },
                {
                  target_id: 2,
                  title: 'Website',
                  icon: 'globe',
                  visits_count: 48,
                },
                {
                  target_id: 3,
                  title: 'Twitter',
                  icon: 'twitter',
                  visits_count: 27,
                },
                {
                  target_id: 4,
                  title: 'Instagram',
                  icon: 'instagram',
                  visits_count: 15,
                },
              ],
              cardVisitsEvolutionByMonth: generateMonthlyData(),
              accessoriesScansEvolutionByMonth: generateMonthlyData(0.6),
              lastActivities: {
                lastCardVisits: {
                  id: 1,
                  card_id: card.id,
                  type: 'card_visit',
                  target_id: null,
                  target_type: null,
                  ip_address: '192.168.1.1',
                  user_agent: 'Mozilla/5.0',
                  referrer: null,
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                },
                lastAccessoriesScans: {
                  id: 2,
                  card_id: card.id,
                  type: 'accessory_scan',
                  target_id: 1,
                  target_type: 'accessory',
                  ip_address: '192.168.1.2',
                  user_agent: 'Mozilla/5.0',
                  referrer: null,
                  created_at: new Date(Date.now() - 86400000).toISOString(), // 1 day ago
                  updated_at: new Date(Date.now() - 86400000).toISOString(),
                },
                lastCardLinkVisits: {
                  id: 3,
                  card_id: card.id,
                  type: 'link_click',
                  target_id: 1,
                  target_type: 'link',
                  ip_address: '192.168.1.3',
                  user_agent: 'Mozilla/5.0',
                  referrer: null,
                  created_at: new Date(Date.now() - 172800000).toISOString(), // 2 days ago
                  updated_at: new Date(Date.now() - 172800000).toISOString(),
                },
              },
            };

            // Set the fake data in the store
            useStatisticsStore.setState({
              cardStatistics: fakeData,
              isLoading: false,
            });
          } else {
            // Handle other errors normally
            handleError(error, undefined, navigate);
          }
        }
      }
    };

    fetchData();
  }, [card, fetchCardStatistics, navigate]);

  const handleUpgrade = () => {
    navigate(`/${i18n.language}/dashboard/pricing`);
  };

  if (isLoading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loading />
      </div>
    );
  }

  const totalAccessoriesScans =
    cardStatistics?.accessoriesScansCount?.reduce(
      (acc, curr) => acc + (curr.scan_count || 0),
      0,
    ) ?? 0;

  const totalLinksClicks =
    cardStatistics?.cardLinksCount?.reduce(
      (acc, curr) => acc + (curr.visits_count || 0),
      0,
    ) ?? 0;

  const visitsData =
    cardStatistics?.cardVisitsEvolutionByMonth?.map(item => ({
      ...item,
      count: Number(item.count) || 0,
    })) ?? [];

  const accessoriesData =
    cardStatistics?.accessoriesScansEvolutionByMonth?.map(item => ({
      ...item,
      count: Number(item.count) || 0,
    })) ?? [];

  return (
    <div className="relative space-y-8">
      {/* Statistiques principales */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <StatCard
          title={t('statistics.cards.visits')}
          value={Number(cardStatistics?.cardVisitsCount) || 0}
        />
        <StatCard
          title={t('statistics.cards.accessoriesScans')}
          value={totalAccessoriesScans}
        />
        <StatCard
          title={t('statistics.cards.linksClicks')}
          value={totalLinksClicks}
        />
      </div>

      {/* Graphiques d'évolution */}
      <div className="space-y-10">
        <div>
          <h2 className="mb-4 text-lg font-medium text-gray-800">
            {t('statistics.chart.visits.title')}
          </h2>
          <div className="h-[300px] min-h-[300px] w-full rounded-lg border border-gray-200 bg-white p-4">
            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
              <LineChart
                data={visitsData}
                margin={{ left: -15, right: 15, top: 10, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis
                  dataKey="month"
                  tickFormatter={formatMonth}
                  stroke="#94a3b8"
                  axisLine={{ stroke: '#e2e8f0' }}
                  tick={{ fill: '#64748b' }}
                />
                <YAxis
                  stroke="#94a3b8"
                  axisLine={{ stroke: '#e2e8f0' }}
                  tick={{ fill: '#64748b' }}
                  width={35}
                  tickMargin={5}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: '0.5rem',
                    border: '1px solid #e2e8f0',
                    boxShadow: 'none',
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{ fill: '#3b82f6', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div>
          <h2 className="mb-4 text-lg font-medium text-gray-800">
            {t('statistics.chart.accessories.title')}
          </h2>
          <div className="h-[300px] min-h-[300px] w-full rounded-lg border border-gray-200 bg-white p-4">
            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
              <LineChart
                data={accessoriesData}
                margin={{ left: -15, right: 15, top: 10, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis
                  dataKey="month"
                  tickFormatter={formatMonth}
                  stroke="#94a3b8"
                  axisLine={{ stroke: '#e2e8f0' }}
                  tick={{ fill: '#64748b' }}
                />
                <YAxis
                  stroke="#94a3b8"
                  axisLine={{ stroke: '#e2e8f0' }}
                  tick={{ fill: '#64748b' }}
                  width={35}
                  tickMargin={5}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: '0.5rem',
                    border: '1px solid #e2e8f0',
                    boxShadow: 'none',
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{ fill: '#3b82f6', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Répartition des clics sur les liens */}
      {cardStatistics?.cardLinksCount &&
        cardStatistics.cardLinksCount.length > 0 && (
          <div>
            <h2 className="mb-4 text-lg font-medium text-gray-800">
              {t('statistics.chart.links.title')}
            </h2>
            <div className="h-[380px] min-h-[380px] w-full rounded-lg border border-gray-200 bg-white p-3 md:h-[450px] md:min-h-[450px] md:p-4">
              <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                <PieChart>
                  <Pie
                    data={cardStatistics.cardLinksCount}
                    cx="50%"
                    cy="36%"
                    labelLine={false}
                    outerRadius={isMobile ? 75 : 85}
                    innerRadius={0}
                    paddingAngle={1}
                    fill="#8884d8"
                    dataKey="visits_count"
                    nameKey="title"
                  >
                    {cardStatistics.cardLinksCount.map((_, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomPieTooltip />} />
                  <Legend
                    content={<CustomLegend />}
                    verticalAlign="bottom"
                    height={70}
                    layout="horizontal"
                    align="center"
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

      <div>
        <h2 className="mb-4 text-lg font-medium text-gray-800">
          {t('statistics.lastActivities.title')}
        </h2>
        {cardStatistics?.lastActivities?.lastCardVisits ||
        cardStatistics?.lastActivities?.lastAccessoriesScans ||
        cardStatistics?.lastActivities?.lastCardLinkVisits ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {cardStatistics?.lastActivities?.lastCardVisits && (
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <h3 className="text-sm font-medium text-gray-600">
                  {t('statistics.lastActivities.lastVisit')}
                </h3>
                <p className="mt-2 text-sm text-gray-800">
                  {new Date(
                    cardStatistics.lastActivities.lastCardVisits.created_at,
                  ).toLocaleString()}
                </p>
              </div>
            )}
            {cardStatistics?.lastActivities?.lastAccessoriesScans && (
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <h3 className="text-sm font-medium text-gray-600">
                  {t('statistics.lastActivities.lastScan')}
                </h3>
                <p className="mt-2 text-sm text-gray-800">
                  {new Date(
                    cardStatistics.lastActivities.lastAccessoriesScans.created_at,
                  ).toLocaleString()}
                </p>
              </div>
            )}
            {cardStatistics.lastActivities?.lastCardLinkVisits && (
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <h3 className="text-sm font-medium text-gray-600">
                  {t('statistics.lastActivities.lastClick')}
                </h3>
                <p className="mt-2 text-sm text-gray-800">
                  {new Date(
                    cardStatistics.lastActivities.lastCardLinkVisits.created_at,
                  ).toLocaleString()}
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-lg border border-gray-200 bg-white p-4 text-center">
            <p className="text-gray-500">
              {t('statistics.lastActivities.noActivities')}
            </p>
          </div>
        )}
      </div>

      {/* Premium overlay */}
      <PremiumStatisticsOverlay
        isVisible={!hasActiveSubscription}
        onUpgrade={handleUpgrade}
      />
    </div>
  );
};
